/**
 * BLE Manager wrapper using react-native-ble-plx
 * Provides an API similar to react-native-ble-manager to minimize code changes
 */

import {BleManager as PlxBleManager} from 'react-native-ble-plx';
import {Platform, PermissionsAndroid, NativeModules} from 'react-native';

// Use console.log instead of DLog to avoid circular dependencies
const DLog = (...args) => {
  if (__DEV__) {
    console.log('[BleManagerPlx]', ...args);
  }
};

// Lazy singleton instance - only created when first accessed
let bleManager = null;

// Get or create the BLE manager instance
const getBleManager = () => {
  if (!bleManager) {
    DLog('Creating PlxBleManager instance for ACON devices');
    bleManager = new PlxBleManager();
  }
  return bleManager;
};

// Event listeners registry
const eventListeners = {
  BleManagerDidUpdateValueForCharacteristic: [],
  BleManagerDiscoverPeripheral: [],
  BleManagerStopScan: [],
  BleManagerDisconnectPeripheral: [],
  BleManagerConnectPeripheral: [],
  BleManagerPeripheralDidBond: [], // For Android pairing events
  BleManagerDidUpdateState: [], // Bluetooth state changes
};

// Store connected devices
const connectedDevices = new Map();

const bluetoothBondingModule =
  NativeModules && NativeModules.BluetoothBonding
    ? NativeModules.BluetoothBonding
    : null;

// Base64 encoding/decoding for React Native
const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';

// Helper to convert base64 to byte array
const base64ToByteArray = base64String => {
  if (!base64String) {
    return [];
  }

  let bufferLength = base64String.length * 0.75;
  if (base64String[base64String.length - 1] === '=') {
    bufferLength--;
    if (base64String[base64String.length - 2] === '=') {
      bufferLength--;
    }
  }

  const bytes = new Array(bufferLength);
  let p = 0;

  for (let i = 0; i < base64String.length; i += 4) {
    const encoded1 = base64Chars.indexOf(base64String[i]);
    const encoded2 = base64Chars.indexOf(base64String[i + 1]);
    const encoded3 = base64Chars.indexOf(base64String[i + 2]);
    const encoded4 = base64Chars.indexOf(base64String[i + 3]);

    bytes[p++] = (encoded1 << 2) | (encoded2 >> 4);
    bytes[p++] = ((encoded2 & 15) << 4) | (encoded3 >> 2);
    bytes[p++] = ((encoded3 & 3) << 6) | (encoded4 & 63);
  }

  return bytes.slice(0, bufferLength);
};

// Helper to convert byte array to base64
const byteArrayToBase64 = byteArray => {
  if (!byteArray || byteArray.length === 0) {
    return '';
  }

  let base64 = '';
  for (let i = 0; i < byteArray.length; i += 3) {
    const byte1 = byteArray[i];
    const byte2 = i + 1 < byteArray.length ? byteArray[i + 1] : 0;
    const byte3 = i + 2 < byteArray.length ? byteArray[i + 2] : 0;

    const encoded1 = byte1 >> 2;
    const encoded2 = ((byte1 & 3) << 4) | (byte2 >> 4);
    const encoded3 = ((byte2 & 15) << 2) | (byte3 >> 6);
    const encoded4 = byte3 & 63;

    base64 += base64Chars[encoded1];
    base64 += base64Chars[encoded2];
    base64 += i + 1 < byteArray.length ? base64Chars[encoded3] : '=';
    base64 += i + 2 < byteArray.length ? base64Chars[encoded4] : '=';
  }

  return base64;
};

const fetchBondState = async deviceId => {
  if (!deviceId || Platform.OS !== 'android' || !bluetoothBondingModule) {
    return 'unknown';
  }

  try {
    const state = await bluetoothBondingModule.getBondState(deviceId);
    return typeof state === 'string' ? state : 'unknown';
  } catch (error) {
    DLog('BleManagerPlx: Error fetching bond state:', error?.message || error);
    return 'unknown';
  }
};

const fetchNativeBondedDevices = async () => {
  if (Platform.OS !== 'android' || !bluetoothBondingModule) {
    return [];
  }

  try {
    const result = await bluetoothBondingModule.listBondedDevices();
    if (!Array.isArray(result)) {
      return [];
    }

    return result
      .filter(item => item && item.id)
      .map(item => ({
        id: item.id,
        name: item.name ?? null,
        bondState: typeof item.bondState === 'string' ? item.bondState : 'unknown',
      }));
  } catch (error) {
    DLog('BleManagerPlx: Error listing bonded devices:', error?.message || error);
    return [];
  }
};

/**
 * Normalize BLE UUID to full 128-bit format
 * Handles both short form (1808) and full form (00001808-0000-1000-8000-00805f9b34fb)
 */
const normalizeUUID = uuid => {
  if (!uuid) return '';
  const cleaned = uuid.toLowerCase().replace(/-/g, '');

  // If it's a short 4-digit UUID, expand to full 128-bit format
  if (cleaned.length === 4) {
    return `0000${cleaned}-0000-1000-8000-00805f9b34fb`;
  }

  // If it's already a full 128-bit UUID (32 hex digits), format with dashes
  if (cleaned.length === 32) {
    return `${cleaned.substring(0, 8)}-${cleaned.substring(8, 12)}-${cleaned.substring(12, 16)}-${cleaned.substring(16, 20)}-${cleaned.substring(20, 32)}`;
  }

  // Return as-is if already formatted correctly
  return uuid.toLowerCase();
};

/**
 * BLE Manager wrapper class
 */
class BleManagerPlx {
  /**
   * Initialize BLE manager
   */
  static async start(options = {}) {
    DLog('BleManagerPlx: Initializing...');

    // Check if Bluetooth is enabled
    const state = await getBleManager().state();
    DLog(`BleManagerPlx: Bluetooth state: ${state}`);

    if (state !== 'PoweredOn') {
      DLog('BleManagerPlx: Bluetooth is not powered on');
    }

    // Helper to emit state to listeners
    const emitState = (bleState) => {
      const stateForListeners = bleState === 'PoweredOn' ? 'on' : 'off';
      eventListeners.BleManagerDidUpdateState.forEach(listener => {
        listener({state: stateForListeners});
      });
    };

    // Emit initial state
    emitState(state);

    // Monitor state changes
    getBleManager().onStateChange(newState => {
      DLog(`BleManagerPlx: Bluetooth state changed to: ${newState}`);
      emitState(newState);
    }, true); // true = emit current state immediately

    DLog('BleManagerPlx: Initialized successfully');
    return Promise.resolve();
  }

  /**
   * Check if Bluetooth is enabled
   */
  static async checkState() {
    const state = await getBleManager().state();
    return state === 'PoweredOn' ? 'on' : 'off';
  }

  /**
   * Request Android BLE permissions (Android 12+)
   */
  static async requestPermissions() {
    if (Platform.OS === 'android' && Platform.Version >= 31) {
      DLog('BleManagerPlx: Requesting Android 12+ BLE permissions...');

      const granted = await PermissionsAndroid.requestMultiple([
        PermissionsAndroid.PERMISSIONS.BLUETOOTH_SCAN,
        PermissionsAndroid.PERMISSIONS.BLUETOOTH_CONNECT,
      ]);

      const scanGranted = granted['android.permission.BLUETOOTH_SCAN'] === 'granted';
      const connectGranted = granted['android.permission.BLUETOOTH_CONNECT'] === 'granted';

      DLog(`BleManagerPlx: BLUETOOTH_SCAN: ${scanGranted ? 'granted' : 'denied'}`);
      DLog(`BleManagerPlx: BLUETOOTH_CONNECT: ${connectGranted ? 'granted' : 'denied'}`);

      return scanGranted && connectGranted;
    }
    return true;
  }

  /**
   * Scan for BLE devices
   * @param serviceUUIDs - Array of service UUIDs to filter by (empty = all devices)
   * @param seconds - Duration in seconds (0 = continuous scan, no auto-stop)
   * @param allowDuplicates - Whether to report the same device multiple times
   * @param scanningOptions - Additional scan options
   */
  static scan(serviceUUIDs = [], seconds = 10, allowDuplicates = false, scanningOptions = {}) {
    const scanMode = seconds === 0 ? 'continuous' : `${seconds} seconds`;
    DLog(`BleManagerPlx: Starting scan (${scanMode}, allowDuplicates: ${allowDuplicates})...`);
    DLog(`BleManagerPlx: Service UUIDs: ${JSON.stringify(serviceUUIDs)}`);

    // Pass null to scan for all devices, or array of UUIDs to filter
    const uuids = Array.isArray(serviceUUIDs) && serviceUUIDs.length > 0 ? serviceUUIDs : null;

    getBleManager().startDeviceScan(uuids, {allowDuplicates}, (error, device) => {
      if (error) {
        DLog('BleManagerPlx: Scan error:', error.message);
        return;
      }

      if (device) {
        // Emit discovery event
        const peripheral = {
          id: device.id,
          name: device.name,
          rssi: device.rssi,
          advertising: {
            localName: device.localName,
            serviceUUIDs: device.serviceUUIDs,
          },
        };

        eventListeners.BleManagerDiscoverPeripheral.forEach(listener => {
          listener(peripheral);
        });
      }
    });

    // Only auto-stop if seconds > 0 (seconds = 0 means continuous scan)
    if (seconds > 0) {
      setTimeout(() => {
        this.stopScan();
      }, seconds * 1000);
    }

    return Promise.resolve();
  }

  /**
   * Stop scanning
   */
  static stopScan() {
    DLog('BleManagerPlx: Stopping scan...');
    getBleManager().stopDeviceScan();

    // Emit stop scan event
    eventListeners.BleManagerStopScan.forEach(listener => {
      listener();
    });

    return Promise.resolve();
  }

  /**
   * Connect to a device
   */
  static async connect(peripheralId) {
    DLog(`BleManagerPlx: Connecting to device: ${peripheralId}`);

    try {
      const device = await getBleManager().connectToDevice(peripheralId, {
        timeout: 30000,
        // Don't request MTU during connect - it can trigger pairing prematurely
      });

      DLog(`BleManagerPlx: Connected to device: ${peripheralId}`);

      // Discover all services and characteristics
      await device.discoverAllServicesAndCharacteristics();
      DLog(`BleManagerPlx: Services and characteristics discovered for: ${peripheralId}`);

      // On Android, check if this is a Bionime device
      if (Platform.OS === 'android') {
        try {
          const services = await device.services();
          const bionimeService = services.find(s => s.uuid.includes('fee0'));

          if (bionimeService) {
            DLog(`BleManagerPlx: Found Bionime service on Android`);
            DLog(`BleManagerPlx: Allowing Android bonding flow to complete before notifications`);
          } else {
            // For non-Bionime devices, request MTU
            try {
              const mtu = await device.requestMTU(517);
              DLog(`BleManagerPlx: MTU set to ${mtu}`);
            } catch (mtuError) {
              DLog(`BleManagerPlx: MTU request failed (non-critical): ${mtuError.message}`);
            }
          }
        } catch (servicesError) {
          DLog(`BleManagerPlx: Error checking services: ${servicesError.message}`);
        }
      }

      // Store device
      connectedDevices.set(peripheralId, device);

      // Monitor disconnection
      device.onDisconnected((error, disconnectedDevice) => {
        DLog(`BleManagerPlx: Device disconnected: ${disconnectedDevice.id}`);
        connectedDevices.delete(disconnectedDevice.id);

        // Emit disconnect event
        eventListeners.BleManagerDisconnectPeripheral.forEach(listener => {
          listener({peripheral: disconnectedDevice.id});
        });
      });

      // Emit connect event
      eventListeners.BleManagerConnectPeripheral.forEach(listener => {
        listener({peripheral: peripheralId});
      });

      return Promise.resolve();
    } catch (error) {
      DLog(`BleManagerPlx: Connection failed for ${peripheralId}:`, error);
      throw error;
    }
  }

  /**
   * Disconnect from a device
   */
  static async disconnect(peripheralId) {
    DLog(`BleManagerPlx: Disconnecting from device: ${peripheralId}`);

    try {
      await getBleManager().cancelDeviceConnection(peripheralId);
      connectedDevices.delete(peripheralId);
      DLog(`BleManagerPlx: Disconnected from device: ${peripheralId}`);
      return Promise.resolve();
    } catch (error) {
      DLog(`BleManagerPlx: Disconnect error for ${peripheralId}:`, error);
      throw error;
    }
  }

  /**
   * Retrieve services for a device
   * In ble-plx, services are already discovered during connection
   */
  static async retrieveServices(peripheralId) {
    DLog(`BleManagerPlx: Retrieving services for: ${peripheralId}`);

    const device = connectedDevices.get(peripheralId);
    if (!device) {
      throw new Error(`Device not connected: ${peripheralId}`);
    }

    try {
      const services = await device.services();
      DLog(`BleManagerPlx: Found ${services.length} services for: ${peripheralId}`);

      // Return in ble-manager compatible format
      return Promise.resolve({
        services: services.map(s => ({uuid: s.uuid})),
        characteristics: [],
      });
    } catch (error) {
      DLog(`BleManagerPlx: Error retrieving services for ${peripheralId}:`, error);
      throw error;
    }
  }

  /**
   * Start notifications for a characteristic
   */
  static async startNotification(peripheralId, serviceUUID, characteristicUUID) {
    DLog(`BleManagerPlx: Starting notifications for ${peripheralId}`);
    DLog(`  Service: ${serviceUUID}`);
    DLog(`  Characteristic: ${characteristicUUID}`);

    const device = connectedDevices.get(peripheralId);
    if (!device) {
      throw new Error(`Device not connected: ${peripheralId}`);
    }

    // Normalize UUIDs to full format for react-native-ble-plx
    // The library may require full 128-bit UUIDs
    const normalizedServiceUUID = normalizeUUID(serviceUUID);
    const normalizedCharUUID = normalizeUUID(characteristicUUID);

    DLog(`  Normalized Service: ${normalizedServiceUUID}`);
    DLog(`  Normalized Characteristic: ${normalizedCharUUID}`);

    // Check characteristic properties to see if encryption/authentication is required
    try {
      const services = await device.services();
      const targetService = services.find(s => s.uuid === normalizedServiceUUID);
      if (targetService) {
        const characteristics = await targetService.characteristics();
        const targetChar = characteristics.find(c => c.uuid === normalizedCharUUID);
        if (targetChar) {
          DLog(`BleManagerPlx: Characteristic properties:`, {
            isReadable: targetChar.isReadable,
            isWritableWithResponse: targetChar.isWritableWithResponse,
            isWritableWithoutResponse: targetChar.isWritableWithoutResponse,
            isNotifiable: targetChar.isNotifiable,
            isIndicatable: targetChar.isIndicatable,
          });
          DLog(`BleManagerPlx: If monitoring triggers pairing, this characteristic requires encryption`);
        }
      }
    } catch (propError) {
      DLog(`BleManagerPlx: Could not read characteristic properties:`, propError.message);
    }

    try {
      // Monitor characteristic for notifications/indications
      const subscription = device.monitorCharacteristicForService(
        normalizedServiceUUID,
        normalizedCharUUID,
        (error, characteristic) => {
          if (error) {
            // Silently ignore disconnect errors - they're expected when meter disconnects after sending data
            const errorMsg = error?.message || '';
            if (!errorMsg.includes('disconnected') && !errorMsg.includes('cancelled')) {
              DLog(`BleManagerPlx: Notification error on ${characteristicUUID}:`, error);
            }
            return;
          }

          if (characteristic && characteristic.value) {
            const data = {
              peripheral: peripheralId,
              characteristic: characteristicUUID,
              service: serviceUUID,
              value: base64ToByteArray(characteristic.value),
            };

            DLog(`BleManagerPlx: Notification received on ${characteristicUUID} (${data.value.length} bytes)`);

            // Emit update event
            eventListeners.BleManagerDidUpdateValueForCharacteristic.forEach(listener => {
              listener(data);
            });
          } else {
            DLog(`BleManagerPlx: Notification received on ${characteristicUUID} but no value`);
          }
        },
      );

      DLog(`BleManagerPlx: Notifications started for ${characteristicUUID} (subscription active)`);
      return Promise.resolve();
    } catch (error) {
      DLog(`BleManagerPlx: Error starting notifications:`, error);
      throw error;
    }
  }

  /**
   * Stop notifications for a characteristic
   */
  static async stopNotification(peripheralId, serviceUUID, characteristicUUID) {
    DLog(`BleManagerPlx: Stopping notifications for ${peripheralId}`);
    DLog(`  Service: ${serviceUUID}`);
    DLog(`  Characteristic: ${characteristicUUID}`);

    // In ble-plx, we need to cancel the subscription
    // This is handled by removing the monitor, which happens when we disconnect
    return Promise.resolve();
  }

  /**
   * Write to a characteristic
   */
  static async write(peripheralId, serviceUUID, characteristicUUID, data, maxByteSize) {
    DLog(`BleManagerPlx: Writing to characteristic ${characteristicUUID}`);
    DLog(`  Data: ${data.map(b => '0x' + b.toString(16).padStart(2, '0')).join(' ')}`);

    const device = connectedDevices.get(peripheralId);
    if (!device) {
      throw new Error(`Device not connected: ${peripheralId}`);
    }

    try {
      // Convert byte array to base64
      const base64Data = byteArrayToBase64(data);

      // Write with response (default)
      await device.writeCharacteristicWithResponseForService(
        serviceUUID,
        characteristicUUID,
        base64Data,
      );

      DLog(`BleManagerPlx: Write successful for ${characteristicUUID}`);
      return Promise.resolve();
    } catch (error) {
      // Silently ignore disconnect errors - they're expected when meter disconnects after sending data
      const errorMsg = error?.message || '';
      if (!errorMsg.includes('disconnected') && !errorMsg.includes('cancelled')) {
        DLog(`BleManagerPlx: Write error:`, error);
        DLog(`BleManagerPlx: Error details:`, {
          message: error.message,
          reason: error.reason,
          code: error.errorCode,
          deviceId: peripheralId,
          service: serviceUUID,
          characteristic: characteristicUUID,
        });
      }
      throw error;
    }
  }

  /**
   * Write to a characteristic without response
   */
  static async writeWithoutResponse(peripheralId, serviceUUID, characteristicUUID, data, maxByteSize) {
    DLog(`BleManagerPlx: Writing without response to ${characteristicUUID}`);

    const device = connectedDevices.get(peripheralId);
    if (!device) {
      throw new Error(`Device not connected: ${peripheralId}`);
    }

    try {
      const base64Data = byteArrayToBase64(data);

      await device.writeCharacteristicWithoutResponseForService(
        serviceUUID,
        characteristicUUID,
        base64Data,
      );

      DLog(`BleManagerPlx: Write without response successful`);
      return Promise.resolve();
    } catch (error) {
      DLog(`BleManagerPlx: Write without response error:`, error);
      throw error;
    }
  }

  /**
   * Write to a descriptor
   */
  static async writeDescriptor(peripheralId, serviceUUID, characteristicUUID, descriptorUUID, data) {
    DLog(`BleManagerPlx: Writing to descriptor ${descriptorUUID}`);
    DLog(`  Data: ${data.map(b => '0x' + b.toString(16).padStart(2, '0')).join(' ')}`);

    const device = connectedDevices.get(peripheralId);
    if (!device) {
      throw new Error(`Device not connected: ${peripheralId}`);
    }

    try {
      const base64Data = byteArrayToBase64(data);

      await device.writeDescriptorForService(
        serviceUUID,
        characteristicUUID,
        descriptorUUID,
        base64Data,
      );

      DLog(`BleManagerPlx: Descriptor write successful`);
      return Promise.resolve();
    } catch (error) {
      DLog(`BleManagerPlx: Descriptor write error:`, error);
      throw error;
    }
  }

  /**
   * Read from a characteristic
   */
  static async read(peripheralId, serviceUUID, characteristicUUID) {
    DLog(`BleManagerPlx: Reading characteristic ${characteristicUUID}`);

    const device = connectedDevices.get(peripheralId);
    if (!device) {
      throw new Error(`Device not connected: ${peripheralId}`);
    }

    try {
      const characteristic = await device.readCharacteristicForService(
        serviceUUID,
        characteristicUUID,
      );

      const data = base64ToByteArray(characteristic.value);
      DLog(`BleManagerPlx: Read ${data.length} bytes from ${characteristicUUID}`);

      return Promise.resolve(data);
    } catch (error) {
      DLog(`BleManagerPlx: Read error:`, error);
      throw error;
    }
  }

  /**
   * Read from a descriptor
   */
  static async readDescriptor(peripheralId, serviceUUID, characteristicUUID, descriptorUUID) {
    DLog(`BleManagerPlx: Reading descriptor ${descriptorUUID}`);

    const device = connectedDevices.get(peripheralId);
    if (!device) {
      throw new Error(`Device not connected: ${peripheralId}`);
    }

    try {
      const descriptor = await device.readDescriptorForService(
        serviceUUID,
        characteristicUUID,
        descriptorUUID,
      );

      const data = base64ToByteArray(descriptor.value);
      DLog(`BleManagerPlx: Read ${data.length} bytes from descriptor`);

      return Promise.resolve(data);
    } catch (error) {
      DLog(`BleManagerPlx: Descriptor read error:`, error);
      throw error;
    }
  }

  /**
   * Request MTU (Android only)
   */
  static async requestMTU(peripheralId, mtu) {
    DLog(`BleManagerPlx: Requesting MTU of ${mtu} bytes for ${peripheralId}`);

    if (Platform.OS !== 'android') {
      DLog('BleManagerPlx: MTU request not supported on iOS');
      return Promise.resolve(23); // Default MTU on iOS
    }

    const device = connectedDevices.get(peripheralId);
    if (!device) {
      throw new Error(`Device not connected: ${peripheralId}`);
    }

    try {
      const result = await device.requestMTU(mtu);
      DLog(`BleManagerPlx: MTU negotiated to ${result.mtu} bytes`);
      return Promise.resolve(result.mtu);
    } catch (error) {
      DLog(`BleManagerPlx: MTU request error:`, error);
      throw error;
    }
  }

  /**
   * Get connected devices
   */
  static async getConnectedPeripherals(serviceUUIDs = []) {
    DLog('BleManagerPlx: Getting connected peripherals...');

    // Always pass an array (even if empty) to avoid null errors in native code
    const uuids = Array.isArray(serviceUUIDs) && serviceUUIDs.length > 0 ? serviceUUIDs : [];
    const devices = await getBleManager().connectedDevices(uuids);

    const peripherals = devices.map(device => ({
      id: device.id,
      name: device.name,
      rssi: device.rssi,
    }));

    DLog(`BleManagerPlx: Found ${peripherals.length} connected devices`);
    return Promise.resolve(peripherals);
  }

  /**
   * Check if a peripheral is connected
   */
  static async isPeripheralConnected(peripheralId, serviceUUIDs = []) {
    DLog(`BleManagerPlx: Checking if ${peripheralId} is connected...`);

    try {
      const device = connectedDevices.get(peripheralId);
      if (device) {
        const isConnected = await device.isConnected();
        DLog(`BleManagerPlx: Device ${peripheralId} connected: ${isConnected}`);
        return Promise.resolve(isConnected);
      }

      DLog(`BleManagerPlx: Device ${peripheralId} not in cache, checking system...`);
      // Always pass an array (even if empty) to avoid null errors in native code
      const uuids = Array.isArray(serviceUUIDs) && serviceUUIDs.length > 0 ? serviceUUIDs : [];
      const devices = await getBleManager().connectedDevices(uuids);

      const found = devices.find(d => d.id === peripheralId);
      const isConnected = !!found;
      DLog(`BleManagerPlx: Device ${peripheralId} connected: ${isConnected}`);
      return Promise.resolve(isConnected);
    } catch (error) {
      DLog(`BleManagerPlx: Error checking connection:`, error);
      return Promise.resolve(false);
    }
  }

  /**
   * Get bonded peripherals (Android only)
   * Uses native Bluetooth adapter to retrieve true bonded devices
   */
  static async getBondedPeripherals() {
    DLog('BleManagerPlx: Getting bonded peripherals (Android)...');

    if (Platform.OS !== 'android') {
      DLog('BleManagerPlx: getBondedPeripherals not supported on iOS');
      return Promise.resolve([]);
    }

    try {
      const [connectedDevicesList, bondedFromNative] = await Promise.all([
        getBleManager().connectedDevices([]),
        fetchNativeBondedDevices(),
      ]);

      const bondedMap = new Map();
      bondedFromNative.forEach(device => {
        bondedMap.set(device.id, device);
      });

      const peripherals = connectedDevicesList.map(device => {
        const bondedInfo = bondedMap.get(device.id);
        if (bondedInfo) {
          bondedMap.delete(device.id);
        }

        return {
          id: device.id,
          name: device.name,
          rssi: device.rssi,
          bondState: bondedInfo?.bondState ?? 'unknown',
        };
      });

      bondedMap.forEach((info, deviceId) => {
        peripherals.push({
          id: deviceId,
          name: info?.name ?? null,
          rssi: null,
          bondState: info?.bondState ?? 'bonded',
        });
      });

      DLog(`BleManagerPlx: Found ${peripherals.length} bonded/connected devices (native bonded: ${bondedFromNative.length})`);
      return Promise.resolve(peripherals);
    } catch (error) {
      DLog(`BleManagerPlx: Error getting bonded peripherals:`, error);
      return Promise.resolve([]);
    }
  }

  /**
   * Get bond state for a specific device (Android only)
   */
  static async getBondState(peripheralId) {
    const state = await fetchBondState(peripheralId);
    return Promise.resolve(state);
  }

  /**
   * Create bond with a device (Android only)
   * Note: react-native-ble-plx doesn't have explicit bonding API
   * Bonding happens automatically during connection when required
   * This method attempts connection which will trigger pairing dialog
   */
  static async createBond(peripheralId, pin = null) {
    DLog(`BleManagerPlx: Creating bond with ${peripheralId}...`);

    if (Platform.OS !== 'android') {
      DLog('BleManagerPlx: createBond not needed on iOS');
      return Promise.resolve();
    }

    try {
      // In react-native-ble-plx, bonding happens automatically when you connect
      // to a device that requires pairing. Just attempt to connect.
      DLog('BleManagerPlx: Attempting connection to trigger bonding...');

      // Don't actually connect here - just resolve
      // The bonding will happen when connect() is called
      DLog('BleManagerPlx: Bond will be created during connection');
      return Promise.resolve();
    } catch (error) {
      DLog(`BleManagerPlx: Bond creation error:`, error);
      throw error;
    }
  }

  /**
   * Add event listener (similar to EventEmitter.addListener)
   */
  static addListener(eventName, handler) {
    if (eventListeners[eventName]) {
      eventListeners[eventName].push(handler);

      return {
        remove: () => {
          const index = eventListeners[eventName].indexOf(handler);
          if (index !== -1) {
            eventListeners[eventName].splice(index, 1);
          }
        },
      };
    }

    DLog(`BleManagerPlx: Unknown event: ${eventName}`);
    return {remove: () => {}};
  }

  /**
   * Remove all listeners for an event
   */
  static removeAllListeners(eventName) {
    if (eventListeners[eventName]) {
      eventListeners[eventName] = [];
    }
  }
}

export default BleManagerPlx;
